/**
 * 视频加载状态枚举
 */
enum VideoReadyState {
  HAVE_NOTHING = 0, // 没有关于音频/视频是否就绪的信息
  HAVE_METADATA = 1, // 关于音频/视频就绪的元数据
  HAVE_CURRENT_DATA = 2, // 关于当前播放位置的数据是可用的，但没有足够的数据来播放下一帧/毫秒
  HAVE_FUTURE_DATA = 3, // 当前及至少下一帧的数据是可用的
  HAVE_ENOUGH_DATA = 4, // 可用数据足以开始播放
}

/**
 * 视频加载配置选项
 */
export interface LoadVideoOptions {
  /** 需要达到的最小 readyState，默认为 HAVE_ENOUGH_DATA (4) */
  minReadyState?: VideoReadyState;
  /** 超时时间（毫秒），默认为 10000ms */
  timeout?: number;
}

/**
 * 加载视频直到达到指定的 readyState
 * @param video - HTML 视频元素
 * @param options - 配置选项
 * @returns Promise<void>
 */
export const loadVideo = async (video: HTMLVideoElement, options: LoadVideoOptions = {}): Promise<void> => {
  const { minReadyState = VideoReadyState.HAVE_ENOUGH_DATA, timeout = 10000 } = options;

  if (video.readyState >= minReadyState) return;

  return new Promise<void>((resolve, reject) => {
    let timeoutId: NodeJS.Timeout | null = null;

    const cleanup = () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('error', handleError);
      if (timeoutId) clearTimeout(timeoutId);
    };

    const handleLoadedData = () => {
      if (video.readyState >= minReadyState) {
        cleanup();
        resolve();
      }
    };

    const handleError = () => {
      cleanup();
      reject(new Error('视频加载失败'));
    };

    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error('视频加载超时'));
    }, timeout);

    video.addEventListener('loadeddata', handleLoadedData, { once: true });
    video.addEventListener('error', handleError, { once: true });
  });
};
